import { inject, Injectable } from '@angular/core';
import type * as angular from 'angular';

import { IrisMetricsReporter } from './iris-metrics-reporter';
import type { ImageLoadState, IrisStatusResponse, LoadContext, QueuedRequest } from './iris.interface';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import { IrisStrategyService } from '@app/service/iris/iris-strategy.service';
import type { ErrorLogService, StatsdService } from '@app/interface/common.interface';
import type { IJobStore } from '@app/interface/job/job-store.interface';
import type { EventAction } from '@app/interface/action/event-action.interface';

@Injectable({
  providedIn: 'root'
})
export class IrisService {
  static readonly STATUS_LOADING = 'loading';
  static readonly STATUS_LOADED = 'loaded';
  static readonly STATUS_FAILED = 'failed';
  static readonly IRIS_STATUS_RENDERED = 'RENDERED';
  static readonly IRIS_STATUS_RENDERING = 'RENDERING';
  static readonly MAX_RETRY_ATTEMPTS = 60;
  static readonly REASON_MAX_RETRY_ATTEMPTS_EXCEEDED = 'Max retry attempts exceeded';
  static readonly REASON_UNEXPECTED_IRIS_STATUS = 'Unexpected error status';
  static readonly CAUSE_IRIS_STATUS_RENDERING = `IRIS status: ${IrisService.IRIS_STATUS_RENDERING}`;
  static readonly CAUSE_IRIS_AUTH_UNAVAILABLE = 'IRIS authorization is unavailable';
  static readonly CAUSE_IRIS_STATUS_CALL_FAILED = 'IRIS status call failed';
  static readonly CAUSE_IRIS_TOKEN_REFRESH_FAILED = 'IRIS bearer token refresh failed';
  static readonly CAUSE_IRIS_INVALID_URL = 'IRIS URL contains null session segment';
  static readonly CAUSE_SESSION_TIMEOUT = 'Session timed out';

  private imagePromises = new Map<string, angular.IPromise<string>>();
  private queue: QueuedRequest[] = [];
  private refreshing = false;
  private cleared = false;

  $q: angular.IQService = inject<angular.IQService>(angularjs.$q);
  $timeout: angular.ITimeoutService = inject<angular.ITimeoutService>(angularjs.$timeout);
  errorLogService: ErrorLogService = inject<ErrorLogService>(angularjs.ErrorLogService);
  statsdService: StatsdService = inject<StatsdService>(angularjs.StatsdService);
  jobStore: IJobStore = inject<IJobStore>(angularjs.JobStore);
  irisStrategyService: IrisStrategyService = inject<IrisStrategyService>(IrisStrategyService);
  metricsReporter: IrisMetricsReporter = inject<IrisMetricsReporter>(IrisMetricsReporter);
  private readonly sessionTimeoutAction: EventAction = inject<EventAction>(angularjs.SessionTimeoutAction);
  private readonly sessionRecreatedAction: EventAction = inject(angularjs.SessionRecreatedAction);

  constructor() {
    this.sessionTimeoutAction.register(() => this.clearAll());
    this.sessionRecreatedAction.register(() => {
      this.cleared = false;
    });
  }

  init(bearerTokenURL: string) {
    this.cleared = false;
    this.irisStrategyService.refresh(bearerTokenURL).catch((error: unknown) => {
      this.errorLogService(`${IrisService.CAUSE_IRIS_TOKEN_REFRESH_FAILED}: ${error}`);
    });
  }

  clearAll(): void {
    this.cleared = true;
    this.imagePromises.clear();
    this.setRefreshing(false);
    this.queue.forEach((item) => item.deferred.reject(IrisService.CAUSE_SESSION_TIMEOUT));
    this.queue = [];
  }

  async load(url: string, bearerTokenURL: string, attrs: { [key: string]: string } = {}): Promise<string> {
    const cached = this.imagePromises.get(url);
    if (cached) return cached;

    if (this.cleared) {
      return this.$q.reject(IrisService.CAUSE_SESSION_TIMEOUT) as angular.IPromise<string>;
    }

    const loadContext: LoadContext = {
      job: this.jobStore.getJob(),
      startTime: new Date(),
      lookNumber: attrs.ltLookNum || attrs.lookNumber || ''
    };

    const earlyFailReason = this.validateBeforeLoad(url, bearerTokenURL);
    if (earlyFailReason) {
      const promise = this.fail({ url, attempts: 0, loadContext, bearerTokenURL }, earlyFailReason);
      this.imagePromises.set(url, promise);
      return promise;
    }

    const promise = this.fetchStatusUntilComplete(url, loadContext, bearerTokenURL);

    this.imagePromises.set(url, promise);
    return promise;
  }

  private validateBeforeLoad(url: string, bearerTokenURL: string): string | null {
    if (!bearerTokenURL) return IrisService.CAUSE_IRIS_AUTH_UNAVAILABLE;
    try {
      const segments = new URL(url).pathname.split('/');
      if (segments.some((s) => s === 'null' || s === 'undefined')) {
        return IrisService.CAUSE_IRIS_INVALID_URL;
      }
    } catch {
      // malformed URL — let fetch fail naturally
    }
    return null;
  }

  private isRefreshing(): boolean {
    return this.refreshing;
  }

  private setRefreshing(refreshing: boolean): void {
    this.refreshing = refreshing;
  }

  private enqueue(request: QueuedRequest): void {
    this.queue.push(request);
  }

  private processAll(processor: (item: QueuedRequest) => void): void {
    this.queue.forEach(processor);
    this.queue = [];
  }

  private failAll(failer: (item: QueuedRequest) => void): void {
    this.queue.forEach(failer);
    this.queue = [];
  }

  private fetchStatusUntilComplete(
    url: string,
    loadContext: LoadContext,
    bearerTokenURL: string
  ): angular.IPromise<string> {
    const state: ImageLoadState = { url, attempts: 0, loadContext, bearerTokenURL };
    return this.fetchStatus(state, { allowRetry: true, allowReAuth: true });
  }

  private async fetchStatus(
    state: ImageLoadState,
    options: { allowRetry: boolean; allowReAuth: boolean }
  ): Promise<string> {
    if (this.cleared) {
      return this.$q.reject(IrisService.CAUSE_SESSION_TIMEOUT) as angular.IPromise<string>;
    }
    if (this.isRefreshing()) {
      return this.queueForAuth(state);
    }
    if (!this.irisStrategyService.isTokenAvailable(state.bearerTokenURL!)) {
      return this.handleUnauthorized(state);
    }

    try {
      const response = await this.irisStrategyService.fetchStatus(state.url, state.bearerTokenURL!);
      // Do NOT await. With return await, when handleStatus's $q promise rejects (e.g., after exhausting retries),
      // the await unwraps that rejection into this catch block at every level of the recursive retry stack.
      // The catch calls handleStatusError → retryLater → fail() again, generating one duplicate error log per
      // retry level (up to 62 total). Without await, the rejection bypasses the catch and propagates directly
      // to the caller — producing exactly one error log.
      // eslint-disable-next-line @typescript-eslint/return-await
      return this.handleStatus(state, response);
    } catch (error) {
      if (typeof error === 'string' && error.startsWith(IrisService.REASON_UNEXPECTED_IRIS_STATUS)) {
        throw error;
      }
      return this.handleStatusError(state, error as { status: number; statusText?: string }, options);
    }
  }

  private handleStatus(state: ImageLoadState, response: IrisStatusResponse): angular.IPromise<string> {
    if (response.status === IrisService.IRIS_STATUS_RENDERED) {
      if (!response.signedUrl) {
        return this.fail(state, `${IrisService.REASON_UNEXPECTED_IRIS_STATUS}: RENDERED without signedUrl`);
      }
      this.metricsReporter.reportSuccess(state.loadContext);
      return this.$q.resolve(response.signedUrl);
    } else if (response.status === IrisService.IRIS_STATUS_RENDERING) {
      return this.retryLater(state, IrisService.CAUSE_IRIS_STATUS_RENDERING);
    }
    return this.fail(state, `${IrisService.REASON_UNEXPECTED_IRIS_STATUS}: ${response.status}`);
  }

  private handleStatusError(
    state: ImageLoadState,
    error: { status: number; statusText?: string },
    options: { allowRetry?: boolean; allowReAuth?: boolean } = {}
  ): angular.IPromise<string> {
    const { allowRetry = true, allowReAuth = true } = options;
    if (allowReAuth && error.status === 401) {
      return this.handleUnauthorized(state);
    }
    const reason = `${IrisService.CAUSE_IRIS_STATUS_CALL_FAILED}: ${error.status}, ${error.statusText ?? ''}`;
    if (allowRetry && IrisService.isRetryableStatus(error.status)) {
      return this.retryLater(state, reason);
    }
    return this.fail(state, reason);
  }

  /**
   * A retry only helps when the failure is transient. 5xx, 429 and status 0 (network error /
   * timeout / CORS) qualify; every other 4xx is a permanent answer about this URL, and retrying
   * it 60 times at 1s intervals only multiplies the failed requests and the error logs.
   * 401 never reaches here — handleStatusError re-authorizes instead.
   */
  private static isRetryableStatus(status: number): boolean {
    return status >= 500 || status === 429 || !status || status < 0;
  }

  private retryLater(state: ImageLoadState, cause: string): angular.IPromise<string> {
    if (this.irisStrategyService.shouldRetry(state, IrisService.MAX_RETRY_ATTEMPTS)) {
      return this.irisStrategyService.scheduleRetry(state, () =>
        this.fetchStatus(state, { allowRetry: true, allowReAuth: true })
      );
    }
    return this.fail(state, `${IrisService.REASON_MAX_RETRY_ATTEMPTS_EXCEEDED}. ${cause}`);
  }

  private fail(state: ImageLoadState, reason: string): angular.IPromise<string> {
    this.metricsReporter.reportFailure(state.loadContext, reason);

    const deferred = this.$q.defer<string>();
    deferred.reject(reason);
    return deferred.promise;
  }

  private queueForAuth(state: ImageLoadState): angular.IPromise<string> {
    const deferred = this.$q.defer<string>();
    this.enqueue({ state, deferred });
    return deferred.promise;
  }

  private async handleUnauthorized(state: ImageLoadState): Promise<string> {
    const deferred = this.$q.defer<string>();
    this.enqueue({ state, deferred });

    if (this.isRefreshing()) {
      return deferred.promise;
    }

    this.setRefreshing(true);
    try {
      await this.irisStrategyService.refresh(state.bearerTokenURL!);
      this.setRefreshing(false);
      this.processAll((item) => {
        this.fetchStatus(item.state, {
          allowRetry: false,
          allowReAuth: false
        })
          .then((signedUrl) => item.deferred.resolve(signedUrl))
          .catch((err) => item.deferred.reject(err));
      });
    } catch {
      this.setRefreshing(false);
      this.failAll((item) => {
        const reason = IrisService.CAUSE_IRIS_TOKEN_REFRESH_FAILED;
        this.metricsReporter.reportFailure(item.state.loadContext, reason);
        item.deferred.reject(reason);
      });
    }
    return deferred.promise;
  }
}
